import {
    Chart,
    Dashboard,
    DashboardSchema,
    DataObj,
    KPI,
    PatchOperation
} from '@/contexts/AdminContext/dashboardProvider';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import Skeleton from '@common/components/Skeleton';
import { Icon, Tooltip } from '@common/index';
import { PenIcon } from '@phosphor-icons/react';
import {
    Check,
    LayoutTemplate,
    Trash2, TrendingDown, TrendingUp,
    Undo
} from 'lucide-react';
import type { Layout, LayoutItem } from 'react-grid-layout';
import { GridLayout, useContainerWidth } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

import { useAdminContext } from '@/contexts/AdminContext';
import { supabase } from '@common/api/authService';
import clsx from 'clsx';
import dayjs from 'dayjs';
import React, {
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import { createPortal } from 'react-dom';
import { replaceCreatedAtFilter } from '../../utils/replaceCreatedAtFilter';
import { DateRange, DateRangePicker } from '../DateRangePicker';
import { ChartRenderer } from './ChartRenderer';
import { extractNumber } from './dashboardUtils';
import DataSourceIcons from './DataSourceIcons';
import { DataSyncLoading } from './DataSyncLoading';
import styles from './index.module.scss';
import MetricTooltip from './MetricTooltip';
import Modal from './Modal';
import modalStyles from './Modal.module.scss';
import { extractDataSources } from './utils/extractDataSources';

interface GridItem {
  key: string;
  type: 'kpi' | 'chart';
  data: KPI | Chart;
}

type SingleLayout = Layout;

interface DashboardUIProps {
  schema: DashboardSchema;
  dateRange?: DateRange;
  title?: string;
  createdAt?: string;
  selectedVersion?: Dashboard | null;
  isGenerating: boolean;
  showCreateModal: boolean;
  setShowCreateModal: (show: boolean) => void;
}

export const DashboardUI: React.FC<DashboardUIProps> = ({
    schema, dateRange, createdAt, title = 'Dashboard', selectedVersion, showCreateModal, setShowCreateModal
}) => {
    const { syncStatus, user } = useAuth();
    const { showToast } = useToast();
    const {
        chat: {
            context, setContext, setIsChatVisible, isChatVisible
        },
        dashboard: {
            getDashboards, isEditMode, setIsEditMode,
            editorStates, initEditor, dispatchAction, updateData, undo, redo, saveDashboard, renameDashboard, deleteDashboard,
            activeItemId, setActiveItemId, versions, currentConversationId
        }
    } = useAdminContext();
    
    // Editor State
    const dashboardId = selectedVersion?.id || 'temp';
    const editorState = editorStates[dashboardId];

    const [editingTitle, setEditingTitle] = useState(title);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isSwitching, setIsSwitching] = useState(false);

    useEffect(() => {
        setIsSwitching(true);
        const timer = setTimeout(() => {
            setIsSwitching(false);
        }, 500);
        return () => clearTimeout(timer);
    }, [dashboardId]);
    
    useEffect(() => {
        setEditingTitle(title);
    }, [title]);

    // Initialize editor state if needed
    useEffect(() => {
        if (selectedVersion) {
            initEditor(dashboardId, selectedVersion.schema);
        }
    }, [dashboardId, selectedVersion, initEditor]);

    // Data State
    const localSchema = editorState?.currentSchema || schema;
    
    const [localDateRange, setLocalDateRange] = useState<DateRange | undefined>(dateRange);
    const [lastUpdateTime, setLastUpdateTime] = useState<Date | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(false);
    
    const [layout, setLayout] = useState<SingleLayout>([] as unknown as Layout);
    const layoutRef = useRef(layout);
    layoutRef.current = layout;

    const dashboardRef = useRef<HTMLDivElement>(null);
    const initDoneRef = useRef(false);
    const { width: containerWidth, containerRef } = useContainerWidth({ initialWidth: 1200 });
    const renderDelayRef = useRef<number | null>(null);
    const [chartsReady, setChartsReady] = useState(false);

    // 响应式列数计算 - 只保留 12 和 9 两个断点
    // 编辑模式下始终使用 12 列
    const responsiveCols = useMemo(() => {
        if (isEditMode) return 12;
        if (containerWidth >= 900) return 12;
        return 9;
    }, [containerWidth, isEditMode]);

    const isDirty = editorState?.isDirty || false;
    const currentAction = editorState?.actions[editorState.pointer];
    const isImport = currentAction?.payload.some(op => op.op === 'IMPORT');
    const canUndo = (editorState?.pointer ?? -1) >= 0 && !isImport;
    const canRedo = (editorState?.pointer ?? -1) < (editorState?.actions.length ?? 0) - 1;

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
                e.preventDefault();
                if (e.shiftKey) {
                    redo(dashboardId);
                } else {
                    undo(dashboardId);
                }
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [dashboardId, undo, redo]);

    // 自动滚动到激活的元素（新增、更新、删除前）
    useEffect(() => {
        if (activeItemId) {
            // Give a small delay for render to complete
            setTimeout(() => {
                const element = document.getElementById(`dashboard-item-${activeItemId}`);

                if (element) {
                    // Use scrollIntoView for automatic scrolling
                    element.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                    });

                    // Highlight effect using CSS class instead of inline styles
                    // Add class to the wrapper
                    element.classList.add(styles.activating);

                    // Also add class to the gridItem for more reliable styling
                    // Use firstElementChild to get the gridItem div directly
                    const gridItem = element.firstElementChild;
                    if (gridItem) {
                        gridItem.classList.add(styles.activating);
                    }

                    setTimeout(() => {
                        element.classList.remove(styles.activating);
                        if (gridItem) {
                            gridItem.classList.remove(styles.activating);
                        }
                    }, 2000);
                }
                setActiveItemId(null);
            }, 500); // 1000ms delay to ensure rendering/layout is stable
        }
    }, [activeItemId, setActiveItemId]);

    // schema 变化时 更新上下文
    useEffect(() => {
        if(localSchema) {
            setContext([{
                title: title,
                payload: {
                    dashboardSchema: localSchema,
                    dateRange: localDateRange,
                },
                type: 'dashboard',
            }]);
        }
        return () => {
            setContext([]);
        }
    }, [localSchema, localDateRange]);

    // 初始化数据源 - 从 SQL 中提取数据源（只执行一次）
    useEffect(() => {
        if (!localSchema) return;

        const hasSources = (schema: DashboardSchema) => {
            const kpiHasSources = (schema.kpi || []).every(kpi => kpi.sources && kpi.sources.length > 0);
            const chartHasSources = (schema.chart || []).every(chart => chart.sources && chart.sources.length > 0);
            return kpiHasSources && chartHasSources;
        };

        // 如果已经有 sources，就不再提取
        if (hasSources(localSchema)) return;

        const newSchema = JSON.parse(JSON.stringify(localSchema)) as DashboardSchema;
        let updated = false;

        // 为 KPI 提取数据源
        if (newSchema.kpi) {
            newSchema.kpi.forEach(kpi => {
                if (kpi.sql && !kpi.sources) {
                    kpi.sources = extractDataSources(kpi.sql);
                    if (kpi.sources && kpi.sources.length > 0) {
                        updated = true;
                    }
                }
            });
        }

        // 为 Chart 提取数据源
        if (newSchema.chart) {
            newSchema.chart.forEach(chart => {
                if (chart.sql && !chart.sources) {
                    chart.sources = extractDataSources(chart.sql);
                    if (chart.sources && chart.sources.length > 0) {
                        updated = true;
                    }
                }
            });
        }

        // 如果有更新，更新到 state
        if (updated) {
            updateData(dashboardId, newSchema);
        }
    }, [localSchema, dashboardId, updateData]);

    // context 被清空时，填充上下文
    useEffect(() => {
        if (context.length === 0 && localSchema) {
            setContext([{
                title: title,
                payload: {
                    dashboardSchema: localSchema,
                    dateRange: localDateRange,
                },
                type: 'dashboard',
            }]);
        }
    }, [context, localSchema, localDateRange]);

    // schema 变化初始化数据
    useEffect(() => {
        console.log('syncStatus', {
            syncCompleted: syncStatus.syncCompleted,
            isEditMode,
            initDone: initDoneRef.current,
            localSchema,
            localDateRange,
        });
        if (!syncStatus.syncCompleted) return;
        if(isEditMode) return;
        if (initDoneRef.current) return;
        if (!localSchema || (!localSchema.kpi && !localSchema.chart)) return;
        if (!localDateRange?.startDate || !localDateRange?.endDate) return;

        const start = dayjs(localDateRange.startDate).startOf('day');
        const end = dayjs(localDateRange.endDate).endOf('day');
        const days = Math.max(0, end.diff(start, 'day'));

        const now = dayjs();
        const newEnd = now.toDate();
        const newStart = now.subtract(days, 'day').toDate();

        initDoneRef.current = true;
        handleDateRangeChange({ startDate: newStart, endDate: newEnd });
    }, [localSchema, localDateRange, syncStatus.syncCompleted, isEditMode]);

    const getPreviousRange = (range: DateRange): DateRange => {
        const start = dayjs(range.startDate);
        const end = dayjs(range.endDate);
        const diffDays = end.diff(start, 'day');
        const prevEnd = start;
        const prevStart = start.subtract(diffDays + 1, 'day');
        return { startDate: prevStart.toDate(), endDate: prevEnd.toDate() };
    };
    
    const handleDateRangeChange = async (range: DateRange) => {
        // 使用当前时间作为结束时间，而不是当天的 23:59:59
        const now = new Date();
        if (range.endDate && dayjs(range.endDate).isSame(now, 'day')) {
            // 如果选择的结束日期是今天，使用当前时间
            range.endDate = now;
        }
        setLocalDateRange(range);
        if (!range.startDate || !range.endDate) return;

        setIsLoading(true);
        try {
            const newSchema = JSON.parse(JSON.stringify(localSchema)) as DashboardSchema;
            const promises: any[] = [];

            // Update KPIs
            if (newSchema.kpi) {
                newSchema.kpi.forEach(kpi => {
                    if (kpi.sql) {
                        const newSql = replaceCreatedAtFilter(kpi.sql, range.startDate!, range.endDate!);
                        kpi.sql = newSql;

                        const p = supabase.rpc("run_select", { query: newSql }).then(({ data, error }) => {
                            if (!error && data) {
                                if (Array.isArray(data)) {
                                    kpi.data = data as any;
                                }
                            }else{
                                console.error("KPI fetch error:", error);
                                kpi.data = []
                            }
                        });
                        promises.push(p);

                        const prevRange = getPreviousRange(range);
                        const prevSql = replaceCreatedAtFilter(newSql, prevRange.startDate!, prevRange.endDate!);
                        const pPrev = supabase.rpc("run_select", { query: prevSql }).then(({ data, error }) => {
                            if (!error && data) {
                                if (Array.isArray(data)) {
                                    kpi.prevData = data;
                                }
                            } else {
                                kpi.prevData = [];
                            }
                        });
                        promises.push(pPrev);
                    } else {
                        console.log("Skipping KPI fetch (no SQL):", kpi.title);
                    }
                });
            }

            // Update Charts
            if (newSchema.chart) {
                newSchema.chart.forEach(chart => {
                    if (chart.sql) {
                        const newSql = replaceCreatedAtFilter(chart.sql, range.startDate!, range.endDate!);
                        chart.sql = newSql;

                        const p = supabase.rpc("run_select", { query: newSql }).then(({ data, error }) => {
                            if (!error && data) {
                                if (Array.isArray(data)) {
                                    chart.data = data as any;
                                }
                            }else{
                                console.error("Chart fetch error:", error);
                                chart.data = []
                            }
                        });
                        promises.push(p);
                    } else {
                        console.log("Skipping Chart fetch (no SQL):", chart.title);
                    }
                });
            }

            await Promise.all(promises);
            updateData(dashboardId, newSchema);

            // 更新时间设置为当前时间
            setLastUpdateTime(now);
        } finally {
            setIsLoading(false);
        }
    };


    const formatDisplayValue = (value: number | string, format?: string, unit?: string): string => {
        if (value === null || value === undefined || value === '') return '-';

        if (format === 'string') {
            return String(value);
        }

        let numVal: number;
        if (typeof value === 'number') {
            numVal = value;
        } else {
            // Handle strings that might be numbers
            const parsed = parseFloat(String(value));
            if (isNaN(parsed)) return String(value);
            numVal = parsed;
        }

        switch (format) {
            case 'currency':
                const currencyStr = numVal.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                });
                if (unit) {
                    const isSymbol = /^[^a-zA-Z0-9]$/.test(unit) || unit === '$' || unit === '¥' || unit === '£' || unit === '€';
                    return isSymbol ? `${unit}${currencyStr}` : `${unit} ${currencyStr}`;
                }
                return currencyStr;

            case 'percent':
                const percentStr = Number.isInteger(numVal)
                    ? numVal.toLocaleString()
                    : numVal.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
                return `${percentStr}${unit || '%'}`;

            case 'number':
            default:
                const numStr = Number.isInteger(numVal)
                    ? numVal.toLocaleString()
                    : numVal.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

                return unit ? `${numStr} ${unit}` : numStr;
        }
    };

    const formatCompactNumber = (value: number): string => {
        if (value === 0) return '0';
        const absValue = Math.abs(value);

        if (absValue >= 1000000000) {
            return `${(value / 1000000000).toFixed(1)}b`;
        }
        if (absValue >= 1000000) {
            return `${(value / 1000000).toFixed(1)}m`;
        }
        if (absValue >= 1000) {
            return `${(value / 1000).toFixed(1)}k`;
        }
        return value.toFixed(1);
    };

    const getFormattedKPIValue = (kpi: KPI) => {
        if (!kpi.data || typeof kpi.data === 'string') return (kpi as any)?.value;
        
        const dataObj = kpi.data as DataObj;
        if (!Array.isArray(dataObj) || dataObj.length === 0) return '-';

        const rawVal = dataObj[0][kpi.column];
        if (rawVal === undefined) return '-';

        const val = extractNumber(rawVal);
        
        return formatDisplayValue(val, kpi.format, kpi.unit);
    };

    const renderKPIDelta = (kpi: KPI) => {
        const currArr = Array.isArray(kpi.data) ? kpi.data : [];
        const prevArr = Array.isArray(kpi.prevData) ? kpi.prevData : [];
        if (currArr.length === 0 || prevArr.length === 0) return null;

        const currRaw = currArr[0][kpi.column];
        const prevRaw = prevArr[0][kpi.column];
        if (currRaw === undefined || prevRaw === undefined) return null;

        const currVal = extractNumber(currRaw);
        const prevVal = extractNumber(prevRaw);
        if (typeof currVal !== 'number' || typeof prevVal !== 'number') return null;

        const diff = currVal - prevVal;
        const pct = prevVal !== 0 ? (diff / prevVal) * 100 : null;
        const isPositive = diff > 0;
        const isNegative = diff < 0;
        const badgeCls = isPositive ? 'up' : (isNegative ? 'down' : 'neutral');
        
        let ArrowIcon = null;
        if (isPositive) ArrowIcon = <TrendingUp size={12} />;
        else if (isNegative) ArrowIcon = <TrendingDown size={12} />;

        const pctStr = pct === null ? '—' : `${Math.abs(pct).toFixed(2)}%`;
        const prevValStr = formatCompactNumber(prevVal);

        return (
            <div className={`${styles.trendBadge} ${styles[badgeCls]}`}>
                {ArrowIcon}
                <span>{`${pctStr} (${prevValStr})`}</span>
            </div>
        );
    };

    const buildLayoutsFromSchema = React.useCallback((sc: DashboardSchema, cols: number = 12) => {
        const items: GridItem[] = [];
        const lg: LayoutItem[] = [];

        const allItems: { key: string, type: 'kpi' | 'chart', data: KPI | Chart, layout?: any }[] = [];

        // Collect all items
        (sc.kpi || []).forEach((k, idx) => {
            allItems.push({
                key: k.id || `kpi-${idx}`, type: 'kpi', data: k, layout: k.layout
            });
        });
        (sc.chart || []).forEach((c, idx) => {
            allItems.push({
                key: c.id || `chart-${idx}`, type: 'chart', data: c, layout: c.layout
            });
        });

        // 9 列模式：按顺序自动布局，忽略原 x、y
        // 12 列模式：使用原有布局
        if (cols === 9) {
            let currentX = 0;
            let currentY = 0;
            let maxYInRow = 0;

            allItems.forEach(item => {
                items.push({
                    key: item.key, type: item.type, data: item.data
                });

                // 确定宽度：KPI 占 3 列，图表占 9 列（满宽）
                const w = item.type === 'kpi' ? 3 : 9;
                const h = item.layout?.h ?? (item.type === 'kpi' ? 2 : 8);

                // 检查是否需要换行
                if (currentX + w > cols) {
                    currentY += maxYInRow;
                    currentX = 0;
                    maxYInRow = 0;
                }

                lg.push({
                    i: item.key,
                    x: currentX,
                    y: currentY,
                    w,
                    h,
                    minW: 2,
                    maxW: 12
                });

                currentX += w;
                maxYInRow = Math.max(maxYInRow, h);
            });
        } else {
            // 12 列模式：有 layout 的元素保持原位置，新增的元素自动计算位置
            const occupiedPositions = new Set<string>();

            // 先标记已有元素占据的位置
            allItems.forEach(item => {
                if (item.layout) {
                    const {
                        x, y, w, h 
                    } = item.layout;
                    for (let dy = 0; dy < h; dy++) {
                        for (let dx = 0; dx < w; dx++) {
                            occupiedPositions.add(`${x + dx},${y + dy}`);
                        }
                    }
                }
            });

            let currentX = 0;
            let currentY = 0;
            let maxYInRow = 0;

            allItems.forEach(item => {
                items.push({
                    key: item.key, type: item.type, data: item.data
                });

                if (item.layout) {
                    // 有布局的元素，使用原布局
                    lg.push({
                        ...item.layout,
                        i: item.key
                    });
                } else {
                    // 没有布局的元素，寻找第一个可用位置
                    const w = item.type === 'kpi' ? 3 : 6;
                    const h = item.type === 'kpi' ? 2 : 8;

                    // 寻找可用位置
                    let foundPosition = false;
                    let targetX = 0;
                    let targetY = 0;

                    // 从左上角开始搜索
                    while (!foundPosition) {
                        while (targetX + w <= 12) {
                            // 检查这个位置是否可用
                            let canPlace = true;
                            for (let dy = 0; dy < h && canPlace; dy++) {
                                for (let dx = 0; dx < w && canPlace; dx++) {
                                    if (occupiedPositions.has(`${targetX + dx},${targetY + dy}`)) {
                                        canPlace = false;
                                        targetX += dx + 1; // 跳过这个被占用的位置
                                        break;
                                    }
                                }
                            }

                            if (canPlace) {
                                foundPosition = true;
                                break;
                            }
                        }

                        if (!foundPosition) {
                            // 当前行没有可用位置，换到下一行
                            targetX = 0;
                            targetY++;
                        }
                    }

                    // 标记新占用的位置
                    for (let dy = 0; dy < h; dy++) {
                        for (let dx = 0; dx < w; dx++) {
                            occupiedPositions.add(`${targetX + dx},${targetY + dy}`);
                        }
                    }

                    lg.push({
                        i: item.key,
                        x: targetX,
                        y: targetY,
                        w,
                        h,
                        minW: 2,
                        maxW: 12
                    });
                }
            });
        }

        const resultLayout: Layout = lg as unknown as Layout;
        return { items, layout: resultLayout };
    }, []);

    const { items, layout: generatedLayout } = useMemo(
        () => buildLayoutsFromSchema(localSchema, responsiveCols),
        [localSchema, responsiveCols, buildLayoutsFromSchema]
    );
    console.log("@@@@ schema", localSchema, layout)
    // Merge layout state with generated layout during render to avoid 1x1 glitch
    const displayLayout = useMemo(() => {
        if (!isEditMode) return generatedLayout;
        
        // Start with current state layout (user's edits), but filter out items that are no longer in generatedLayout (deleted items)
        const generatedIds = new Set(generatedLayout.map(l => l.i));
        const currentLayoutFiltered = layout.filter(l => generatedIds.has(l.i));
        
        const currentLayoutMap = new Map(currentLayoutFiltered.map(l => [l.i, l]));
        const finalLayout = [...currentLayoutFiltered];
        
        // Add any missing items from generatedLayout (newly added items)
        generatedLayout.forEach(gl => {
            if (!currentLayoutMap.has(gl.i)) {
                finalLayout.push(gl);
            }
        });
        
        return finalLayout;
    }, [layout, generatedLayout, isEditMode]);

    // Sync layouts when schema changes
    useEffect(() => {
        if (generatedLayout) {
            if (isEditMode) {
                // In Edit Mode, we only want to sync structural changes (additions/removals)
                // We MUST ignore position changes from generatedLayout because:
                // 1. generatedLayout comes from schema, which has "saved" positions
                // 2. RGL might have auto-compacted items to new positions that are NOT yet in schema
                // 3. If we force generatedLayout positions, RGL will revert, then re-compact, causing infinite loop

                const currentIds = new Set(layoutRef.current.map(i => i.i));
                const generatedIds = new Set(generatedLayout.map(i => i.i));

                let hasStructuralChange = false;
                if (currentIds.size !== generatedIds.size) {
                    hasStructuralChange = true;
                } else {
                    for (const id of generatedIds) {
                        if (!currentIds.has(id)) {
                            hasStructuralChange = true;
                            break;
                        }
                    }
                }

                if (hasStructuralChange) {
                    // Preserve current positions for existing items, only add/remove items
                    const nextLayout = layoutRef.current.filter(l => generatedIds.has(l.i));

                    // Add new items from generatedLayout
                    generatedLayout.forEach(gl => {
                        if (!currentIds.has(gl.i)) {
                            nextLayout.push(gl);
                        }
                    });

                    setLayout(nextLayout);
                }
            } else {
                setLayout(generatedLayout);
            }
        }
    }, [generatedLayout, isEditMode]);

    // When switching to edit mode or responsiveCols changes, reset layout to generatedLayout
    useEffect(() => {
        if (isEditMode && generatedLayout) {
            // 切换到编辑模式时，重置为 12 列的布局
            setLayout(generatedLayout);
        }
    }, [isEditMode, responsiveCols, generatedLayout]);

    useEffect(() => {
        setChartsReady(false);
        if (renderDelayRef.current !== null) {
            clearTimeout(renderDelayRef.current);
        }
        renderDelayRef.current = window.setTimeout(() => setChartsReady(true), 200);
        return () => {
            if (renderDelayRef.current !== null) {
                clearTimeout(renderDelayRef.current);
            }
        };
    }, [containerWidth, layout, isEditMode]);

    const closeCreateModal = () => {
        setShowCreateModal(false);
    };

    const handleAskAi = (type: 'kpi' | 'chart', data: KPI | Chart) => {
        const partialSchema: DashboardSchema = {
            kpi: type === 'kpi' ? [data as KPI] : [],
            chart: type === 'chart' ? [data as Chart] : [],
        };

        setContext([{
            title: data.title,
            payload: {
                dashboardSchema: partialSchema,
                dateRange: localDateRange,
            },
            canDelete: true,
            type: 'dashboard',
        }]);
        setIsChatVisible(true);
    };

    const toggleEditMode = () => {
        setIsEditMode(!isEditMode);
    };

    const handleSave = async () => {
        setIsLoading(true);
        try {
            // 使用 currentConversationId，它会自动从 temp 更新为真实的 conversation ID
            // 只有当 conversationId 不是 'temp' 时才关联
            const conversationId = currentConversationId && currentConversationId !== 'temp'
                ? currentConversationId
                : undefined;

            // saveDashboard now handles both create (if isTemp) and update
            await saveDashboard(dashboardId, editingTitle, conversationId);

            showToast('Dashboard saved successfully!', 'success');
            setIsEditMode(false);

        } catch (error) {
            console.error('Error saving dashboard:', error);
            showToast('Failed to save dashboard', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    // Debounce ref
    const layoutChangeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Resizing state for visual feedback
    // Note: We use pure DOM manipulation for visual feedback during resize to avoid re-renders
    // that would cause react-grid-layout to lose its internal state and reset item position.
    
    const onResizeStart = (layout: any, oldItem: any, newItem: any) => {
        if (newItem) {
            // Add resizing class to the item container
            // The item key corresponds to the div id inside GridLayout if we set it, 
            // but here we are mapping children. We can find the element by data-key or id if available.
            // React-grid-layout wraps children in a div.react-grid-item.
            
            // We'll use a more direct approach: modify styles via the event element if possible, 
            // but RGL doesn't pass the element directly in onResizeStart signature here.
            // So we rely on the badge ID we set in the render loop.
            const badge = document.getElementById(`resize-badge-${newItem.i}`);
            if (badge) {
                // Update text
                badge.innerText = `${newItem.w} × ${newItem.h}`;
                
                // Find the parent grid item to add the .resizing class
                // The structure is: .gridItem -> .resizeOverlay -> .resizeBadge
                // So badge.parentElement is overlay, overlay.parentElement is gridItem
                const overlay = badge.parentElement;
                if (overlay) {
                    overlay.classList.add(styles.visible);
                    const gridItem = overlay.parentElement;
                    if (gridItem) {
                        gridItem.classList.add(styles.resizing);
                    }
                }
            }
        }
    };

    const onResize = (layout: any, oldItem: any, newItem: any) => {
        if (!newItem) return;
        const badge = document.getElementById(`resize-badge-${newItem.i}`);
        if (badge) {
            badge.innerText = `${newItem.w} × ${newItem.h}`;
        }
    };

    const onResizeStop = (layout: any, oldItem: any, newItem: any) => {
        if (!newItem) return;
        const badge = document.getElementById(`resize-badge-${newItem.i}`);
        if (badge) {
            const overlay = badge.parentElement;
            if (overlay) {
                overlay.classList.remove(styles.visible);
                const gridItem = overlay.parentElement;
                if (gridItem) {
                    gridItem.classList.remove(styles.resizing);
                }
            }
        }
    };

    // Dragging state for visual feedback
    const [draggingId, setDraggingId] = useState<string | null>(null);

    const onDragStart = (layout: any, oldItem: any, newItem: any) => {
        setDraggingId(newItem.i);
    };

    const onDragStop = (layout: any, oldItem: any, newItem: any) => {
        setDraggingId(null);
    };

    const onLayoutChange = (currLayout: Layout) => {
        if (!isEditMode) return;

        // 在 9 列模式下，不保存布局更改
        // 因为 9 列模式使用的是自动计算的布局，不应该保存到 schema
        if (responsiveCols !== 12) return;

        // Immediate UI update for smooth dragging
        setLayout(currLayout);

        // Debounce history updates
        if (layoutChangeTimeoutRef.current) {
            clearTimeout(layoutChangeTimeoutRef.current);
        }

        layoutChangeTimeoutRef.current = setTimeout(() => {
            // Check if layout actually changed to avoid duplicate history entries
            // We compare against the current schema's layout state
            const prevLayoutMap = new Map();
            (localSchema.kpi || []).forEach((k, idx) => {
                const key = k.id || `kpi-${idx}`;
                if (k.layout) prevLayoutMap.set(key, k.layout);
            });
            (localSchema.chart || []).forEach((c, idx) => {
                const key = c.id || `chart-${idx}`;
                if (c.layout) prevLayoutMap.set(key, c.layout);
            });

            let hasChange = false;
            // If length differs, it's a change (though usually handled by ADD/DELETE)
            // Main check: positions
            for (const item of currLayout) {
                const prev = prevLayoutMap.get(item.i);
                if (!prev) {
                    // New item that wasn't in schema layout?
                    hasChange = true;
                    break;
                }
                if (prev.x !== item.x || prev.y !== item.y || prev.w !== item.w || prev.h !== item.h) {
                    hasChange = true;
                    break;
                }
            }

            if (!hasChange && currLayout.length !== prevLayoutMap.size) {
                // If items removed/added
                hasChange = true;
            }

            if (hasChange) {
                const ops: PatchOperation[] = [];
                const newLayoutMap = new Map(currLayout.map(l => [l.i, l]));

                // Update KPI layouts
                (localSchema.kpi || []).forEach((k, idx) => {
                    const key = k.id || `kpi-${idx}`;
                    const l = newLayoutMap.get(key);
                    if (l) {
                        ops.push({
                            op: 'UPDATE',
                            path: `kpi[${idx}].layout`,
                            value: {
                                x: l.x, y: l.y, w: l.w, h: l.h
                            }
                        });
                    }
                });

                // Update Chart layouts
                (localSchema.chart || []).forEach((c, idx) => {
                    const key = c.id || `chart-${idx}`;
                    const l = newLayoutMap.get(key);
                    if (l) {
                        ops.push({
                            op: 'UPDATE',
                            path: `chart[${idx}].layout`,
                            value: {
                                x: l.x, y: l.y, w: l.w, h: l.h
                            }
                        });
                    }
                });

                if (ops.length > 0) {
                    dispatchAction(dashboardId, {
                        type: 'BATCH_PATCH',
                        payload: ops,
                        description: 'Layout changed'
                    });
                }
            }
        }, 300); // 300ms debounce
    };

    const handleDelete = (key: string) => {
        
        const ops: PatchOperation[] = [];
        
        // Find and delete from KPI array
        const kpiIndex = (localSchema.kpi || []).findIndex((k, idx) => (k.id || `kpi-${idx}`) === key);
        if (kpiIndex > -1) {
            ops.push({
                op: 'DELETE',
                path: `kpi[${kpiIndex}]`
            });
        }

        // Find and delete from Chart array
        const chartIndex = (localSchema.chart || []).findIndex((c, idx) => (c.id || `chart-${idx}`) === key);
        if (chartIndex > -1) {
            ops.push({
                op: 'DELETE',
                path: `chart[${chartIndex}]`
            });
        }

        if (ops.length > 0) {
            dispatchAction(dashboardId, {
                type: 'BATCH_PATCH',
                payload: ops,
                description: 'Delete item'
            });
        }
    };

    const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEditingTitle(e.target.value);
    };

    return (
        <div className={styles.dashboard} ref={dashboardRef}>
            <div className={styles.header}>
                <div className={styles.title}>
                    {!isEditMode && (
                        <div
                            style={{
                                display: 'flex', alignItems: 'center', gap: '12px' 
                            }}
                        >
                            <h1>{title}</h1>
                            {lastUpdateTime ? (
                                <span
                                    style={{
                                        fontSize: '12px',
                                        color: '#9CA3AF',
                                        fontWeight: 500,
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    Last refreshed:
                                    {' '}
                                    {dayjs(lastUpdateTime).format('h:mm A')}
                                    {' '}
                                    (
                                    {Intl.DateTimeFormat().resolvedOptions().timeZone}
                                    {' '}
                                    Time)
                                </span>
                            ) : null}
                        </div>
                    )}
                </div>
                <div className={styles.actions}>
                    {!isEditMode && (
                    <>
                        {/* Edit Button */}
                        <Tooltip content="Edit Dashboard">
                            <button
                                onClick={toggleEditMode}
                                className={`${styles.iconEditBtn} group`}
                            >
                                <PenIcon size={20} weight="bold" className="text-[#1A162F]" />
                                <div className={styles.editTooltip}>
                                    Edit Dashboard
                                </div>
                            </button>
                        </Tooltip>

                        {/* Date Range Picker */}
                        {localDateRange ? (
                            <DateRangePicker
                                value={localDateRange}
                                onConfirm={handleDateRangeChange}
                                className={styles.dateTrigger}
                            />
                        ) : null}
                    </>
                    )}

                    {isEditMode ? (() => {
                        const container = document.getElementById('admin-edit-header-content');
                        return container ? createPortal(
                            <div
                                className={styles.header}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    width: '100%',
                                }}
                            >
                                <div className={styles.title}>
                                    <input
                                        type="text"
                                        value={editingTitle}
                                        className={styles.editTitleInput}
                                        placeholder="Dashboard Name"
                                        onChange={handleTitleChange}
                                    />
                                    <span className={styles.editingBadge}>EDITING</span>
                                </div>
                                <div className={styles.actions}>
                                    <button
                                        onClick={() => setIsDeleteModalOpen(true)}
                                        className={`${styles.headerBtn} ${styles.undo}`}
                                        title="Delete Dashboard"
                                        style={{ color: '#ef4444' }}
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                    {/* Undo Button */}
                                    <button
                                        onClick={() => undo(dashboardId)}
                                        disabled={!canUndo}
                                        title="Undo (Cmd+Z)"
                                        className={`${styles.headerBtn} ${styles.undo}`}
                                    >
                                        <Undo size={16} />
                                    </button>

                                    {/* Save Button */}
                                    <button
                                        onClick={handleSave}
                                        disabled={dashboardId !== 'temp_dashboard' && !isDirty && editingTitle === title}
                                        className={`${styles.headerBtn} ${styles.save}`}
                                    >
                                        <Check size={16} />
                                        <span>Publish</span>
                                    </button>
                                </div>
                            </div>,
                            container
                        ) : null;
                    })() : null}
                </div>
            </div>
                
            <Modal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                title="Delete Dashboard"
                width="max-w-sm"
            >
                <div className={modalStyles.modalBody}>
                    <p className={modalStyles.modalText}>
                        Are you sure you want to delete 
                        {' '}
                        <span className={modalStyles.modalStrong}>
                            "
                            {title}
                            "
                        </span>
                        ? This action cannot be undone.
                    </p>
                    <div className={modalStyles.modalFooter}>
                        <button
                            onClick={() => setIsDeleteModalOpen(false)}
                            className={modalStyles.modalCancelBtn}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={async () => {
                                await deleteDashboard(dashboardId);
                                setIsDeleteModalOpen(false);
                                setIsEditMode(false);
                            }}
                            className={modalStyles.modalDeleteBtn}
                        >
                            Delete
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Data Sync Loading State */}
            {!syncStatus.syncCompleted && <DataSyncLoading />}

            <div
                ref={containerRef as any}
                className={clsx(
                    isEditMode && styles.editMode,
                    isSwitching && styles.disableAnimation
                )}
                style={!syncStatus.syncCompleted ? { display: 'none' } : {}}
            >
                {items.length === 0 && (
                <div className={styles.emptyState}>
                    <div className={styles.emptyStateIconWrapper}>
                        <LayoutTemplate size={32} />
                    </div>
                    <div className={styles.emptyStateContent}>
                        <h3>Start Building</h3>
                        <p>Ask Bustly AI to create your first metric</p>
                    </div>
                </div>
                )}
                <GridLayout
                    className="layout"
                    layout={displayLayout}
                    gridConfig={{
                        cols: responsiveCols,
                        rowHeight: 68,
                        margin: [16, 16]
                    }}
                    width={containerWidth}
                    dragConfig={{ enabled: isEditMode }}
                    resizeConfig={{ enabled: isEditMode }}
                    onLayoutChange={onLayoutChange}
                    onResizeStart={onResizeStart}
                    onResize={onResize}
                    onResizeStop={onResizeStop}
                    onDragStart={onDragStart}
                    onDragStop={onDragStop}
                >
                    {items.map((item) => (
                        <div
                            key={item.key}
                            id={`dashboard-item-${item.key}`}
                            className={styles.gridItemWrapper}
                        >
                            <div className={`${styles.gridItem} ${item.type === 'kpi' ? styles.kpiItem : styles.chartItem} ${draggingId === item.key ? styles.dragging : ''}`}>
                                {/* KPI Rendering */}
                                {item.type === 'kpi' && (
                                    isLoading ? (
                                        <Skeleton 
                                            width="100%" 
                                            height="100%" 
                                            style={{ borderRadius: 24 }}
                                        />
                                    ) : (
                                        <div className={styles.kpiCard}>
                                            {/* Row 1: Data source icon (left) + Ask AI/Remove button (right) */}
                                            <div className={styles.kpiHeader}>
                                                <DataSourceIcons sources={(item.data as KPI).sources} />
                                                <div className={styles.cardControls}>
                                                    {isEditMode ? (
                                                        <button
                                                            className={styles.deleteButton}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDelete(item.key);
                                                            }}
                                                        >
                                                            <Trash2 size={16} />
                                                            <span className={styles.deleteText}>Remove</span>
                                                        </button>
                                                    ) : (
                                                        <div className={styles.askAi} onClick={() => handleAskAi(item.type, item.data)}>
                                                            <Icon name="sparkles" size={16} />
                                                            <span className={styles.askAiText}>Ask AI</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Row 2: Title with MetricTooltip */}
                                            <div className={styles.kpiContent}>
                                                <div className={styles.kpiTitleRow}>
                                                    <MetricTooltip
                                                        title={item.data.title}
                                                        formula={(item.data as KPI).formula}
                                                        detail={(item.data as KPI).sql_detail}
                                                        sql={(item.data as KPI).sql}
                                                    >
                                                        <span className={styles.kpiTitle}>{item.data.title}</span>
                                                    </MetricTooltip>
                                                </div>

                                                {/* Row 3: Value and trend indicator */}
                                                <div className={styles.kpiValueRow}>
                                                    <span className={styles.kpiValue} title={getFormattedKPIValue(item.data as KPI)}>
                                                        {getFormattedKPIValue(item.data as KPI)}
                                                    </span>
                                                    {renderKPIDelta(item.data as KPI)}
                                                </div>
                                            </div>
                                        </div>
                                    )
                                )}

                                {/* Chart Rendering */}
                                {item.type === 'chart' && (
                                    <div className={styles.chartCard}>
                                        {isLoading ? (
                                            <div
                                                style={{
                                                    height: '100%', display: 'flex', flexDirection: 'column', gap: 16 
                                                }}
                                            >
                                                <Skeleton width="30%" height={24} />
                                                <Skeleton width="100%" height="100%" style={{ flex: 1, borderRadius: 8 }} />
                                            </div>
                                        ) : (
                                            <>
                                                <div className={styles.chartHeader}>
                                                    <div className={styles.chartTitleWrapper}>
                                                        <div
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 8,
                                                                minWidth: 0,
                                                                overflow: 'hidden'
                                                            }}
                                                        >
                                                            <div
                                                                style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    height: '20px',
                                                                    flexShrink: 0
                                                                }}
                                                            >
                                                                <DataSourceIcons
                                                                    sources={(item.data as Chart).sources}
                                                                />
                                                            </div>

                                                            <MetricTooltip
                                                                title={item.data.title}
                                                                formula={(item.data as Chart).formula}
                                                                detail={(item.data as Chart).sql_detail}
                                                                sql={(item.data as Chart).sql}
                                                            >
                                                                <h3>{item.data.title}</h3>
                                                            </MetricTooltip>
                                                        </div>
                                                    </div>
                                                    <div className={styles.cardControls}>
                                                        {isEditMode ? (
                                                            <button
                                                                className={styles.deleteButton}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDelete(item.key);
                                                                }}
                                                            >
                                                                <Trash2 size={16} />
                                                                <span className={styles.deleteText}>Remove</span>
                                                            </button>
                                                        ) : (
                                                            <div className={styles.askAi} onClick={() => handleAskAi(item.type, item.data)}>
                                                                <Icon name="sparkles" size={16} />
                                                                <span className={styles.askAiText}>Ask AI</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className={styles.chartContent}>
                                                    <ChartRenderer
                                                        chart={item.data as Chart}
                                                        colSpan={displayLayout.find((l: any) => l.i === item.key)?.w}
                                                        chartsReady={chartsReady}
                                                    />
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}
                                
                                <div className={styles.resizeOverlay}>
                                    <div id={`resize-badge-${item.key}`} className={styles.resizeBadge}>
                                    </div>
                                    <div className={styles.resizeBorder} />
                                </div>
                            </div>
                        </div>
                    ))}
                </GridLayout>
            </div>
        </div>
    );
};
